import { useEffect, useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import useLatestRef from './creation/shared/useLatestRef';
import { CreationPayload, InitialPayload, readOption, readString } from './creation/shared/payloadReaders';
import { cardSx, sectionTitleSx } from './creation/shared/buttonStyles';

const PRINT_TYPES = ['offset', 'numerique'] as const;
const ORIENTATIONS = ['portrait', 'paysage'] as const;

type PrintType = (typeof PRINT_TYPES)[number];
type Orientation = (typeof ORIENTATIONS)[number];

interface Option<T extends string> {
  value: T;
  label: string;
}

const PRINT_TYPE_OPTIONS: Option<PrintType>[] = [
  { value: 'offset', label: 'Offset' },
  { value: 'numerique', label: 'Numérique' },
];
const ORIENTATION_OPTIONS: Option<Orientation>[] = [
  { value: 'portrait', label: 'Portrait' },
  { value: 'paysage', label: 'Paysage' },
];

const toOptions = (values: string[]): Option<string>[] => values.map(value => ({ value, label: value }));

const FOND_DE_PAGE_OPTIONS = toOptions(['Feuille blanche', 'Fond personnalisé']);
const GRAMMAGE_OPTIONS = toOptions(['80g/m²', '90g/m²', '120g/m²', '170g/m²']);
const TYPE_PAPIER_OPTIONS = toOptions(['Papier classique', 'Papier recyclé', 'Papier couché']);
const DIMENSION_OPTIONS = toOptions(['A4 (210x297mm)', 'A5 (148x210mm)', 'A6 (105x148mm)']);
const ENVELOPPE_OPTIONS = toOptions(['Enveloppe C4', 'Enveloppe C5', 'Enveloppe C6', 'Enveloppe à fenêtre']);

interface SelectFieldProps<T extends string> {
  label: string;
  value: T | '';
  options: Option<T>[];
  onChange: (value: T) => void;
}

function SelectField<T extends string>({ label, value, options, onChange }: SelectFieldProps<T>) {
  return (
    <FormControl size="small" sx={{ width: 220 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={e => onChange(e.target.value as T)}>
        {options.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

interface CourrierCreationSectionProps {
  initialPayload?: InitialPayload;
  onPayloadChange?: (payload: CreationPayload) => void;
}

function CourrierCreationSection({ initialPayload, onPayloadChange }: CourrierCreationSectionProps) {
  const theme = useTheme();
  const [printType, setPrintType] = useState(() => readOption(initialPayload, 'printType', PRINT_TYPES, 'numerique'));
  const [fondDePage, setFondDePage] = useState(() => readString(initialPayload, 'fondDePage'));
  const [grammage, setGrammage] = useState(() => readString(initialPayload, 'grammage'));
  const [typePapier, setTypePapier] = useState(() => readString(initialPayload, 'typePapier'));
  const [dimension, setDimension] = useState(() => readString(initialPayload, 'dimension'));
  const [orientationRecto, setOrientationRecto] = useState(() => readOption(initialPayload, 'orientationRecto', ORIENTATIONS, 'portrait'));
  const [orientationVerso, setOrientationVerso] = useState(() => readOption(initialPayload, 'orientationVerso', ORIENTATIONS, 'portrait'));
  const [enveloppe, setEnveloppe] = useState(() => readString(initialPayload, 'enveloppe'));
  const onPayloadChangeRef = useLatestRef(onPayloadChange);

  useEffect(() => {
    onPayloadChangeRef.current?.({
      type: 'courrier',
      printType, fondDePage, grammage, typePapier, dimension, orientationRecto, orientationVerso, enveloppe,
    });
  }, [printType, fondDePage, grammage, typePapier, dimension, orientationRecto, orientationVerso, enveloppe, onPayloadChangeRef]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={cardSx}>
        <Typography variant="subtitle2" sx={{ ...sectionTitleSx(theme.palette.primary.main), mb: 2 }}>
          <DescriptionIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.75 }} />
          Document
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <SelectField label="Type d'impression" value={printType} options={PRINT_TYPE_OPTIONS} onChange={setPrintType} />
            <SelectField label="Fond de page" value={fondDePage} options={FOND_DE_PAGE_OPTIONS} onChange={setFondDePage} />
            <SelectField label="Grammage" value={grammage} options={GRAMMAGE_OPTIONS} onChange={setGrammage} />
            <SelectField label="Type de papier" value={typePapier} options={TYPE_PAPIER_OPTIONS} onChange={setTypePapier} />
            <SelectField label="Dimension" value={dimension} options={DIMENSION_OPTIONS} onChange={setDimension} />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <SelectField label="Orientation du Recto" value={orientationRecto} options={ORIENTATION_OPTIONS} onChange={setOrientationRecto} />
            <SelectField label="Orientation du Verso" value={orientationVerso} options={ORIENTATION_OPTIONS} onChange={setOrientationVerso} />
            <SelectField label="Enveloppe" value={enveloppe} options={ENVELOPPE_OPTIONS} onChange={setEnveloppe} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CourrierCreationSection;
